import { useEffect, useState } from 'react';
import { FormikProps, useFormik } from 'formik';
import * as Yup from 'yup';

import { useCreateZoneServiceItemMutation } from 'store/apis';
import { useZoneCategoryServiceItems } from 'store/apis/zone-categories/zone-categories.hook';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';
import { useAppSelector } from 'store/store.hook';

interface UseZoneServiceItemsFormManagerProps {
  zoneId: number;
  categoryId?: number | null;
  next?: (data?: any) => void;
}

export const useZoneServiceItemsFormManager = ({
  zoneId,
  categoryId,
  next = () => {},
}: UseZoneServiceItemsFormManagerProps) => {
  const { orgSlug } = useAppSelector(selectCurrentUserInfo);
  const [mutate, { isLoading, isSuccess, isError, ...rest }] = useCreateZoneServiceItemMutation();
  const { data: categoryDefaults } = useZoneCategoryServiceItems({ categoryId });
  const [applyingDefaults, setApplyingDefaults] = useState(false);

  const initialValues = {
    name: '',
  };

  const validationSchema = Yup.object({
    name: Yup.string().nullable().required('This value is required.'),
  });

  const formManager = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (serviceItem, { resetForm }) => {
      resetForm({});
      mutate({ orgSlug, zoneId, serviceItem });
    },
  });

  const applyDefaultsFromCategory = async () => {
    if (!categoryDefaults.length) return;
    setApplyingDefaults(true);
    try {
      await Promise.all(categoryDefaults.map((item) => mutate({ orgSlug, zoneId, serviceItem: { name: item.name } })));
    } finally {
      setApplyingDefaults(false);
    }
  };

  useEffect(() => {
    if (!isLoading && isSuccess && !isError) {
      const { data } = rest;
      next(data);
    }
  }, [isLoading, isSuccess, isError]);

  return [
    {
      formManager,
      loading: isLoading,
      applyingDefaults,
      applyDefaultsFromCategory,
    },
  ] as [
    {
      formManager: FormikProps<typeof initialValues>;
      loading: boolean;
      applyingDefaults: boolean;
      applyDefaultsFromCategory: () => Promise<void>;
    },
  ];
};
